import {
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FieldTimeOutlined,
    MoreOutlined,
    SendOutlined
} from '@ant-design/icons'
import type { Post } from '@entities/post'
import { AddPostModal, EditPostModal, usePosts } from '@entities/post'
import { PreviewPostModal } from '@entities/post/ui/preview-post-modal/preview-post-modal'
import { useClickOutside } from '@shared/hooks/use-click-outside'
import { stripHtml } from '@shared/lib/stripHtml/stripHtml'
import { CopyButton } from '@shared/ui/copy-button/copy-button'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { RBCCalendar } from '@shared/ui/rbc-calendar/rbc-calendar'
import type { RBCEvent } from '@shared/ui/rbc-calendar/types'
import SmallPreviewPost, { PreviewPostText } from '@shared/ui/small-preview-post/small-preview-post'
import WeeklyCalendar from '@shared/ui/weekly-calendar/weekly-calendar'
import type { TablePaginationConfig, TableProps } from 'antd'
import {
    Button,
    Card,
    Col,
    DatePicker,
    Dropdown,
    Flex,
    Input,
    Modal,
    Popconfirm,
    Popover,
    Radio,
    Row,
    Space,
    Table,
    theme,
    Typography
} from 'antd'
import type { ItemType } from 'antd/lib/menu/interface'
import dayjs from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'
import type { SlotInfo } from 'react-big-calendar'
import './styled.scss'
const { RangePicker } = DatePicker
const { Title, Text } = Typography

export enum EDisplayPosts {
    day = 'День',
    week = 'Неделя',
    month = 'Месяц',
    all = 'Все',
    period = 'Период'
}

export const ScheduledPostsPage = () => {
    const [post, setPost] = useState<undefined | Post>()
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const [editPostData, setEditPost] = useState<Post | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const {
        token: { colorLink }
    } = theme.useToken()

    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
    const [isSharedLinkOpen, setIsSharedLinkOpen] = useState(false)
    const [display, setDisplay] = useState<EDisplayPosts>(EDisplayPosts.week)

    const {
        posts,
        dates,
        pagination,
        isPostsLoading,
        sharedData,
        actions: {
            setDates,
            setSharedData,
            clearStateShare,
            setPagination,
            deletePost,
            createPost,
            publishPostNow,
            previewPost,
            editPost,
            createShareLink,
            deleteShareLink
        }
    } = usePosts({ state: 'planned', display })

    const onEditModalOpen = (post: Post) => {
        setEditPost(post)
        setIsEditModalOpen(true)
    }

    const onEditModalClose = () => {
        setEditPost(null)
        setIsEditModalOpen(false)
    }

    const handleTableChange = useCallback(
        (pagination: TablePaginationConfig) => {
            setPagination({
                page: pagination.current || 0,
                pageSize: pagination.pageSize || 0
            })
        },
        [setPagination]
    )

    const onOpenSharedLink = useCallback(
        (post: Post) => {
            if (post.isShared) {
                createShareLink(post.id)
            } else {
                setSharedData({ id: post.id, isShared: post.isShared })
            }
            setIsSharedLinkOpen(true)
        },
        [createShareLink, setSharedData]
    )

    const onCloseSharedLink = () => {
        setIsSharedLinkOpen(false)
        clearStateShare()
    }

    const onCreateSharedLink = () => {
        createShareLink(sharedData.id)
    }

    const onCloseAccessSharedLink = () => {
        deleteShareLink()
    }
    const stopProp = (e: React.MouseEvent) => {
        e.stopPropagation()
    }
    const columns = useMemo(() => {
        const columnsData: TableProps<Post>['columns'] = [
            {
                title: 'Таймслот',
                dataIndex: 'time-slot',
                key: 'time-slot',
                render: (_, record) => {
                    return dayjs(record.publishDate).format('HH:mm')
                }
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                render: (_, record) => {
                    if (record.state === 'planned') {
                        return 'Запланирован'
                    }
                    if (record.state === 'published') {
                        return 'Опубликован'
                    }
                }
            },
            {
                title: 'Медиа',
                dataIndex: 'media',
                key: 'media',
                render: (_, record) => <SmallPreviewPost post={record} />
            },
            {
                title: 'Пост',
                dataIndex: 'post',
                key: 'post',
                render: (_, record) => <PreviewPostText post={record} />
            },
            {
                title: 'Тип',
                dataIndex: 'type',
                key: 'type',
                render: (_, record) => {
                    return (
                        <>
                            {record.postType && (
                                <CustomTag color={record.postType.color}>{record.postType.name}</CustomTag>
                            )}
                        </>
                    )
                }
            },
            {
                title: 'Автор',
                dataIndex: 'author',
                key: 'author',
                render: (_, record) => {
                    return record.creator.username
                }
            },
            {
                title: 'Комментарий',
                dataIndex: 'comment',
                key: 'comment'
            },
            {
                title: 'Действия',
                key: 'action',
                render: (_, record) => (
                    <Space onClick={stopProp} size="middle">
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    {
                                        key: '0',
                                        label: (
                                            <Space
                                                onClick={() => handleSelectEvent(record)}
                                                style={{ color: colorLink }}
                                            >
                                                <EyeOutlined /> Превью поста
                                            </Space>
                                        )
                                    },
                                    {
                                        key: '11',
                                        disabled: record.isAdvertising,
                                        label: (
                                            <Space onClick={() => onEditModalOpen(record)} style={{ color: colorLink }}>
                                                <EditOutlined /> Редактировать
                                            </Space>
                                        )
                                    },
                                    {
                                        key: '1',
                                        label: (
                                            <Popconfirm
                                                title="Создать копию"
                                                description="Вы уверены что хотите создать копию поста?"
                                                onConfirm={() => createPost(record)}
                                                okText="Создать"
                                                cancelText="Отмена"
                                            >
                                                <Space style={{ color: colorLink, width: '100%' }}>
                                                    <CopyOutlined /> Создать копию
                                                </Space>
                                            </Popconfirm>
                                        )
                                    },
                                    {
                                        key: '2',
                                        label: (
                                            <Space
                                                onClick={() => onOpenSharedLink(record)}
                                                style={{ color: colorLink, width: '100%' }}
                                            >
                                                <SendOutlined /> Доступ по ссылке
                                            </Space>
                                        )
                                    },
                                    {
                                        key: '3',
                                        label: (
                                            <Popconfirm
                                                title="Опубликовать сейчас"
                                                description="Вы уверены что хотите опубликовать пост?"
                                                onConfirm={() => publishPostNow(record.id)}
                                                okText="Опубликовать"
                                                cancelText="Отмена"
                                            >
                                                <Space style={{ color: colorLink, width: '100%' }}>
                                                    <SendOutlined /> Опубликовать сейчас
                                                </Space>
                                            </Popconfirm>
                                        )
                                    },
                                    {
                                        key: '4',
                                        label: (
                                            <Space
                                                onClick={() => previewPost(record)}
                                                style={{ color: colorLink, width: '100%' }}
                                            >
                                                <EyeOutlined /> Предпросмотр в бот
                                            </Space>
                                        )
                                    },
                                    record.state === 'planned' && {
                                        key: '5',
                                        label: (
                                            <Popconfirm
                                                title="Снять с очереди"
                                                description="Вы уверены что хотите снять с очереди пост?"
                                                onConfirm={() =>
                                                    editPost({
                                                        ...record,
                                                        state: 'draft'
                                                    })
                                                }
                                                okText="Снять"
                                                cancelText="Отмена"
                                            >
                                                <Space style={{ color: colorLink, width: '100%' }}>
                                                    <FieldTimeOutlined /> Снять с очереди
                                                </Space>
                                            </Popconfirm>
                                        )
                                    },
                                    {
                                        key: '7',
                                        danger: true,
                                        label: (
                                            <Popconfirm
                                                title="Удалить пост"
                                                description="Вы уверены что хотите удалить пост?"
                                                onConfirm={() => deletePost(record.id)}
                                                okText="Удалить"
                                                cancelText="Отмена"
                                            >
                                                <Space style={{ width: '100%' }}>
                                                    <DeleteOutlined /> Удалить
                                                </Space>
                                            </Popconfirm>
                                        )
                                    }
                                ].filter(Boolean) as ItemType[]
                            }}
                        >
                            <MoreOutlined />
                        </Dropdown>
                    </Space>
                )
            }
        ]
        if (display === EDisplayPosts.all || display === EDisplayPosts.period) {
            columnsData.unshift({
                title: 'Дата',
                dataIndex: 'publishDate',
                key: 'date',
                render: (_, record, index) => {
                    const currentDate = dayjs(record.publishDate).format('DD-MM-YYYY')

                    const sameDatePosts =
                        posts?.results.filter((post) => dayjs(post.publishDate).format('DD-MM-YYYY') === currentDate) ||
                        []

                    const firstOccurrenceIndex = posts?.results.findIndex(
                        (post) => dayjs(post.publishDate).format('DD-MM-YYYY') === currentDate
                    )

                    return {
                        children: currentDate,
                        props: index === firstOccurrenceIndex ? { rowSpan: sameDatePosts.length } : { rowSpan: 0 }
                    }
                }
            })
        }
        return columnsData
    }, [
        colorLink,
        createPost,
        deletePost,
        display,
        editPost,
        onOpenSharedLink,
        posts?.results,
        previewPost,
        publishPostNow
    ])

    const postingEvents: RBCEvent[] = useMemo(
        () =>
            posts?.results.map((i) => {
                const title = i.messages
                    .map((message) => {
                        return message.text
                    })
                    .join(' ')
                return {
                    title: stripHtml(title),
                    start: i.publishDate,
                    end: i.publishDate,
                    post: i
                }
            }) ?? [],
        [posts?.results]
    )

    const handleSelectEvent = (post: Post) => {
        setPost(post)
        setIsPreviewModalOpen(true)
    }

    const onSelectDay = (data: SlotInfo) => {
        setDisplay(EDisplayPosts.day)
        setDates({
            startDate: data.start,
            endDate: data.end
        })
    }

    const componentToRender = useMemo(() => {
        switch (display) {
            case EDisplayPosts.day:
                return (
                    <Table<Post>
                        columns={columns}
                        dataSource={posts?.results.sort((a, b) => {
                            if (a.publishDate && b.publishDate) {
                                return new Date(a.publishDate).valueOf() - new Date(b.publishDate).valueOf()
                            }
                            return 0
                        })}
                        loading={isPostsLoading}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            total: posts?.count || 0,
                            showSizeChanger: true
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                handleSelectEvent(record) // Здесь вызов функции при клике по строке
                            }
                        })}
                        scroll={{
                            y: 'calc(100vh - 500px)'
                        }}
                        onChange={handleTableChange}
                        rowKey="id"
                        rowClassName={() => 'custom-row-height'}
                    />
                )
            case EDisplayPosts.week:
                return (
                    <WeeklyCalendar
                        setDisplay={setDisplay}
                        posts={posts?.results}
                        onChange={({ startDate, endDate }) => setDates({ startDate, endDate })}
                        onClick={handleSelectEvent}
                    />
                )
            case EDisplayPosts.all:
                return (
                    <Table<Post>
                        columns={columns}
                        dataSource={posts?.results}
                        loading={isPostsLoading}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            total: posts?.count || 0,
                            showSizeChanger: true
                        }}
                        scroll={{
                            y: 'calc(100vh - 470px)'
                        }}
                        onChange={handleTableChange}
                        onRow={(record) => ({
                            onClick: () => {
                                handleSelectEvent(record) // Здесь вызов функции при клике по строке
                            }
                        })}
                        rowKey="id"
                        // rowClassName={getRowClassName} TODO узнать у евгения
                    />
                )
            case EDisplayPosts.period:
                return (
                    <Table<Post>
                        columns={columns}
                        dataSource={posts?.results}
                        loading={isPostsLoading}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            total: posts?.count || 0,
                            showSizeChanger: true
                        }}
                        onChange={handleTableChange}
                        onRow={(record) => ({
                            onClick: () => {
                                handleSelectEvent(record) // Здесь вызов функции при клике по строке
                            }
                        })}
                        scroll={{
                            y: 'calc(100vh - 441px)'
                        }}
                        rowKey="id"
                        // rowClassName={getRowClassName} TODO узнать у евгения
                    />
                )
            default:
                return (
                    <RBCCalendar
                        onSelectDay={onSelectDay}
                        onClick={handleSelectEvent}
                        events={postingEvents}
                        onChange={({ startDate, endDate }) => setDates({ startDate, endDate })}
                    />
                )
        }
    }, [
        display,
        columns,
        posts?.results,
        posts?.count,
        isPostsLoading,
        pagination.page,
        pagination.pageSize,
        handleTableChange,
        onSelectDay,
        postingEvents,
        setDates
    ])

    const handlePrevDay = () => {
        setDates({
            startDate: dayjs(dates.startDate).subtract(1, 'day').startOf('day').toDate(),
            endDate: dayjs(dates.startDate).subtract(1, 'day').endOf('day').toDate()
        })

        // setCurrentDate((prevDate) => prevDate.subtract(1, 'day'))
    }

    const handleNextDay = () => {
        setDates({
            startDate: dayjs(dates.startDate).add(1, 'day').startOf('day').toDate(),
            endDate: dayjs(dates.startDate).add(1, 'day').endOf('day').toDate()
        })

        // setCurrentDate((prevDate) => prevDate.add(1, 'day'))
    }

    const [isPeriodVisible, setIsPeriodVisible] = useState(false)

    const ref = useClickOutside<HTMLDivElement>((event) => {
        const target = event.target as HTMLElement
        if (!target.closest('.ant-picker-dropdown')) {
            setIsPeriodVisible(false)
        }
    })

    const periodPicker = (
        <div ref={ref}>
            <RangePicker
                onChange={(value) => {
                    setDisplay(EDisplayPosts.period)
                    setDates({
                        startDate: value?.[0]?.toDate() || new Date(),
                        endDate: value?.[1]?.toDate() || new Date()!
                    })
                }}
            />
            <Button style={{ marginLeft: 5 }} onClick={() => setDisplay(EDisplayPosts.all)}>
                {EDisplayPosts.all}
            </Button>
        </div>
    )

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    return (
        <Card>
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Запланированные</Title>
                <Flex gap={8}>
                    <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                        Создать пост
                    </Button>
                    <Radio.Group
                        onChange={(value) => setDisplay(value.target.value)}
                        value={display}
                        defaultValue={display}
                    >
                        <Radio.Button
                            value={EDisplayPosts.day}
                            onClick={() => {
                                setDates({
                                    startDate: dayjs().startOf('day').toDate(),
                                    endDate: dayjs().endOf('day').toDate()
                                })
                            }}
                        >
                            {EDisplayPosts.day}
                        </Radio.Button>
                        <Radio.Button
                            value={EDisplayPosts.week}
                            onClick={() => {
                                setDates({
                                    startDate: dayjs().startOf('week').toDate(),
                                    endDate: dayjs().endOf('week').toDate()
                                })
                            }}
                        >
                            {EDisplayPosts.week}
                        </Radio.Button>
                        <Radio.Button value={EDisplayPosts.month}>{EDisplayPosts.month}</Radio.Button>
                        <Popover
                            content={periodPicker}
                            trigger={'click'}
                            open={isPeriodVisible}
                            placement="bottomRight"
                        >
                            <Radio.Button value={EDisplayPosts.period} onClick={() => setIsPeriodVisible(true)}>
                                Период
                            </Radio.Button>
                        </Popover>
                    </Radio.Group>
                </Flex>
            </Flex>
            {display === EDisplayPosts.day && (
                <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                    <Radio.Group>
                        <Radio.Button onClick={handlePrevDay}>Назад</Radio.Button>
                        <Radio.Button onClick={handleNextDay}>Вперед</Radio.Button>
                    </Radio.Group>
                    <Col flex="auto" style={{ textAlign: 'center', marginRight: 100 }}>
                        <Title level={5} style={{ margin: 0 }}>
                            {dayjs(dates.startDate).startOf('day').format('DD.MM dd')}
                        </Title>
                    </Col>
                    <div />
                </Flex>
            )}

            {componentToRender}

            <Modal
                open={isScheduleModalOpen}
                onCancel={() => setIsScheduleModalOpen(false)}
                onOk={() => setIsScheduleModalOpen(false)}
                centered
                title="Запланировать пост"
                okText="Сохранить"
                cancelText="Отменить"
            >
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <DatePicker
                            showTime
                            showSecond={false}
                            style={{ width: '100%' }}
                            placeholder="Выберите дату публикации"
                        />
                    </Col>
                </Row>
            </Modal>
            <Modal
                open={isSharedLinkOpen}
                onCancel={onCloseSharedLink}
                onOk={sharedData.sharedId || sharedData.isShared ? onCloseAccessSharedLink : onCreateSharedLink}
                centered
                title="Доступ по ссылке"
                okText={sharedData.sharedId || sharedData.isShared ? 'Закрыть доступ' : 'Открыть доступ'}
                cancelText="Отменить"
            >
                {sharedData.sharedId ? (
                    <Space>
                        <Input
                            style={{ width: '300px' }}
                            value={`https://telegaplan.ru/posts/${sharedData.id}/shared/${sharedData.sharedId}`}
                        />
                        <CopyButton
                            textToCopy={`https://telegaplan.ru/posts/${sharedData.id}/shared/${sharedData.sharedId}`}
                        />
                    </Space>
                ) : (
                    <Text>По ссылке будет доступна возможность просматривать пост и делать его копию.</Text>
                )}
            </Modal>
            <PreviewPostModal
                onEditModalOpen={onEditModalOpen}
                post={post}
                open={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
            />
            {isCreateModalOpen && <AddPostModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />}

            {editPostData && (
                <EditPostModal editPostData={editPostData} open={isEditModalOpen} onClose={onEditModalClose} />
            )}
        </Card>
    )
}
